<template>
    
    <div class="workorder-items-datatable">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0"><!-- Line Items --></h6>
            <div class="-btn-group btn-group-sm">
                <button @click="refresh()" class="btn btn-outline-secondary me-2" :disabled="props.disableActions">
                    <i class="fa fa-sync"></i> Refresh
                </button>
                <button @click="openFromJobModal()" class="btn btn-outline-info me-2" :disabled="props.disableActions || !props.requestId">
                    <i class="fa fa-link"></i> From Job
                </button>
                <button @click="addNewRow()" class="btn btn-outline-primary" :disabled="props.disableActions">
                    <i class="fa fa-plus"></i> New
                </button>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-sm table-bordered table-hover">
                <thead class="table-light">
                    <tr>
                        <th style="width: 60px;">Actions</th>
                        <th style="width: 250px;">Description</th>
                        <th style="width: 120px;">Type</th>
                        <th style="width: 80px;">Qty</th>
                        <th style="width: 100px;">Unit Cost</th>
                        <th style="width: 100px;">Discount</th>
                        <th style="width: 60px;">Tax?</th>
                        <th style="width: 80px;">Tax Rate</th>
                        <th style="width: 100px;">Line Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!items.length">
                        <td colspan="9" class="text-center text-muted">
                            No line items yet. Click "New" to add one.
                        </td>
                    </tr>
                    <tr v-for="(item, index) in items" :key="item.Id || 'new-' + index" :class="{ 'table-warning': item._isEditing }">
                        <!-- Actions -->
                        <td>
                            <!-- Editing mode buttons -->
                            <div v-if="item._isEditing" class="-btn-group btn-group-sm" role="group">
                                <button 
                                    @click="saveRow(item)" 
                                    class="btn btn-outline-success me-2"
                                    title="Save"
                                >
                                    <i class="fa fa-check"></i>
                                </button>
                                <button 
                                    @click="cancelEdit(item)" 
                                    class="btn btn-outline-secondary me-2"
                                    title="Cancel"
                                >
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                            
                            <!-- Non-editing mode: button group -->
                            <div v-if="!item._isEditing" class="-btn-group btn-group-sm" role="group">
                                <button 
                                    @click="editRow(item)" 
                                    class="btn btn-outline-primary me-1"
                                    :disabled="props.disableActions"
                                    title="Edit"
                                >
                                    <i class="fa fa-edit"></i>
                                </button>
                                <button 
                                    v-if="item.Id"
                                    @click="scheduleTask(item)" 
                                    class="btn btn-outline-info me-1"
                                    :disabled="props.disableActions"
                                    title="Schedule Task"
                                >
                                    <i class="fa fa-calendar"></i>
                                </button>
                                <button 
                                    v-if="item.Id"
                                    @click="deleteRow(item)" 
                                    class="btn btn-outline-danger"
                                    :disabled="props.disableActions"
                                    title="Delete"
                                >
                                    <i class="fa fa-trash"></i>
                                </button>
                            </div>
                        </td>

                        <!-- Description -->
                        <td>
                            <textarea 
                                v-if="item._isEditing"
                                v-model="item.Description"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Item description"
                            ></textarea>
                            <span v-else>{{ item.Description }}</span>
                        </td>

                        <!-- Type -->
                        <td>
                            <select 
                                v-if="item._isEditing"
                                v-model="item.ItemType"
                                class="form-select form-select-sm"
                            >
                                <option value="Labor">Labor</option>
                                <option value="Part">Part</option>
                                <option value="Service">Service</option>
                                <option value="Other">Other</option>
                            </select>
                            <span v-else>{{ item.ItemType }}</span>
                        </td>

                        <!-- Quantity -->
                        <td>
                            <input 
                                v-if="item._isEditing"
                                v-model.number="item.Quantity"
                                type="number"
                                step="0.01"
                                class="form-control form-control-sm"
                                @input="calculateLineTotals(item)"
                            />
                            <span v-else>{{ item.Quantity }}</span>
                        </td>

                        <!-- Unit Cost -->
                        <td>
                            <input 
                                v-if="item._isEditing"
                                v-model.number="item.UnitCost"
                                type="number"
                                step="0.01"
                                class="form-control form-control-sm"
                                @input="calculateLineTotals(item)"
                            />
                            <span v-else>${{ (item.UnitCost || 0).toFixed(2) }}</span>
                        </td>

                        <!-- Discount -->
                        <td>
                            <input 
                                v-if="item._isEditing"
                                v-model.number="item.DiscountAmount"
                                type="number"
                                step="0.01"
                                class="form-control form-control-sm"
                                @input="calculateLineTotals(item)"
                            />
                            <span v-else>${{ (item.DiscountAmount || 0).toFixed(2) }}</span>
                        </td>

                        <!-- Taxable -->
                        <td class="text-center">
                            <input 
                                v-if="item._isEditing"
                                v-model="item.Taxable"
                                type="checkbox"
                                class="form-check-input"
                                @change="calculateLineTotals(item)"
                            />
                            <i v-else :class="item.Taxable ? 'fa fa-check text-success' : 'fa fa-times text-muted'"></i>
                        </td>

                        <!-- Tax Rate -->
                        <td>
                            <input 
                                v-if="item._isEditing && item.Taxable"
                                v-model.number="item.TaxRate"
                                type="number"
                                step="0.01"
                                class="form-control form-control-sm"
                                @input="calculateLineTotals(item)"
                            />
                            <span v-else-if="item.Taxable">{{ (item.TaxRate || 0).toFixed(2) }}%</span>
                            <span v-else class="text-muted">--</span>
                        </td>

                        <!-- Line Total -->
                        <td class="text-end">
                            <strong>${{ calculateDisplayTotal(item) }}</strong>
                        </td>
                    </tr>
                </tbody>
                <tfoot class="table-light">
                    <tr>
                        <td colspan="8" class="text-end"><strong>Subtotal:</strong></td>
                        <td class="text-end"><strong>${{ totals.subtotal.toFixed(2) }}</strong></td>
                    </tr>
                    <tr>
                        <td colspan="8" class="text-end"><strong>Tax:</strong></td>
                        <td class="text-end"><strong>${{ totals.tax.toFixed(2) }}</strong></td>
                    </tr>
                    <tr class="table-primary">
                        <td colspan="8" class="text-end"><strong>Total:</strong></td>
                        <td class="text-end"><strong>${{ totals.total.toFixed(2) }}</strong></td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <!-- Mobile Edit Modal -->
        <work-order-item-edit
            v-if="isMobile && editingItem"
            :item="editingItem"
            @save="saveRow"
            @cancel="cancelEdit"
        />

        <!-- Assignment/Task Scheduling Modal -->
        <assignment-edit
            v-if="showAssignmentModal"
            :isNew="true"
            :request-id="props.requestId"
            :client-id="props.clientId"
            :work-assignment="selectedItemForSchedule?.Description || ''"
            @close="onAssignmentClose"
        />

        <!-- From Job Modal -->
        <job-to-workorder-item-binning
            v-if="showFromJobModal"
            :request-id="props.requestId"
            :client-id="props.clientId"
            :work-order-id="props.workOrderId"
            @close="onFromJobClose"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import Api from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { WorkOrderItemModel, WorkOrderItemFilter } from '@/models/WorkOrderItemModel';
import AssignmentEdit from '@/components/Time/AssignmentEdit.vue';
import JobToWorkorderItemBinning from '@/components/Jobs/JobToWorkorderItemBinning.vue';

const props = defineProps({
    workOrderId: {
        type: Number,
        required: true
    },
    requestId: {
        type: Number,
        default: null
    },
    clientId: {
        type: Number,
        default: null
    },
    embedded: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['items-updated']);

const items = ref<WorkOrderItemModel[]>([]);
const editingItem = ref<WorkOrderItemModel | null>(null);
const isMobile = ref(false);
const showAssignmentModal = ref(false);
const showFromJobModal = ref(false);
const selectedItemForSchedule = ref<WorkOrderItemModel | null>(null);

// Check if mobile
onMounted(() => {
    if (typeof window !== 'undefined') {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        isMobile.value = mediaQuery.matches;
        mediaQuery.addEventListener('change', (e) => { isMobile.value = e.matches; });
    }
    loadItems();
});

// Load items
const loadItems = async () => {
    try {
        const filter: WorkOrderItemFilter = {
            WorkOrderId: [props.workOrderId],
            Size: 9999  // Load all items for this work order
        };
        
        const response = await Api.loadWorkOrderItems(filter);
        items.value = response.Data || [];
        emitItemsUpdate();
    } catch (error) {
        console.error('Failed to load work order items:', error);
        Alert.notify({ text: 'Failed to load line items', type: 'error' });
    }
};

const refresh = () => {
    loadItems();
};

// Add new row
const addNewRow = () => {
    const newItem = new WorkOrderItemModel();
    newItem.WorkOrderId = props.workOrderId;
    newItem.ItemType = 'Labor';
    newItem.Quantity = 1;
    newItem.UnitCost = 0;
    newItem.Taxable = true;
    newItem.TaxRate = 0;
    newItem._isEditing = true;
    
    items.value.push(newItem);
    console.log('[WorkOrderItemsDatatable] Added new row, _isEditing=true');
};

// Edit row
const editRow = (item: WorkOrderItemModel) => {
    // Cancel any other editing rows
    items.value.forEach(i => {
        if (i._isEditing && i !== item) {
            cancelEdit(i);
        }
    });
    
    // Store original values for cancel
    item._originalValues = { ...item };
    item._isEditing = true;
    editingItem.value = item;
    console.log('[WorkOrderItemsDatatable] Editing row, _isEditing=true');
};

// Calculate line totals
const calculateLineTotals = (item: WorkOrderItemModel) => {
    const qty = item.Quantity || 0;
    const unitCost = item.UnitCost || 0;
    const discount = item.DiscountAmount || 0;
    
    const lineTotal = (qty * unitCost) - discount;
    item.LineTotal = lineTotal;
    
    if (item.Taxable && item.TaxRate) {
        item.TaxAmount = lineTotal * (item.TaxRate / 100);
    } else {
        item.TaxAmount = 0;
    }
};

// Calculate display total
const calculateDisplayTotal = (item: WorkOrderItemModel) => {
    const lineTotal = item.LineTotal || 0;
    const taxAmount = item.TaxAmount || 0;
    return (lineTotal + taxAmount).toFixed(2);
};

// Save row
const saveRow = async (item: WorkOrderItemModel) => {
    try {
        // Validate
        if (!item.Description) {
            Alert.notify({ text: 'Description is required', type: 'error' });
            return;
        }
        
        // Calculate final totals
        calculateLineTotals(item);
        
        // Save to API
        const savedItem = await Api.saveWorkOrderItem(item);
        
        // Update item with saved data
        Object.assign(item, savedItem);
        item._isEditing = false;
        item._originalValues = null;
        editingItem.value = null;
        console.log('[WorkOrderItemsDatatable] Saved row, _isEditing=false');
        
        Toast.success('Line item saved');
        emitItemsUpdate();
    } catch (error) {
        console.error('Failed to save line item:', error);
        Alert.notify({ text: 'Failed to save line item', type: 'error' });
    }
};

// Cancel edit
const cancelEdit = (item: WorkOrderItemModel) => {
    if (item._originalValues) {
        // Restore original values
        Object.assign(item, item._originalValues);
        item._originalValues = null;
    } else {
        // New item that wasn't saved - remove it
        const index = items.value.indexOf(item);
        if (index > -1) {
            items.value.splice(index, 1);
        }
    }
    
    item._isEditing = false;
    editingItem.value = null;
    console.log('[WorkOrderItemsDatatable] Cancelled edit, _isEditing=false');
};

// Delete row
const deleteRow = async (item: WorkOrderItemModel) => {
    if (!confirm('Are you sure you want to delete this line item?')) {
        return;
    }
    
    try {
        await Api.deleteWorkOrderItem(item.Id!);
        const index = items.value.indexOf(item);
        if (index > -1) {
            items.value.splice(index, 1);
        }
        Toast.success('Line item deleted');
        emitItemsUpdate();
    } catch (error) {
        console.error('Failed to delete line item:', error);
        Alert.notify({ text: 'Failed to delete line item', type: 'error' });
    }
};

// Calculate totals
const totals = computed(() => {
    let subtotal = 0;
    let tax = 0;
    
    items.value.forEach(item => {
        const lineTotal = item.LineTotal || 0;
        const taxAmount = item.TaxAmount || 0;
        
        subtotal += lineTotal;
        tax += taxAmount;
    });
    
    return {
        subtotal,
        tax,
        total: subtotal + tax
    };
});

// Emit items update
const emitItemsUpdate = () => {
    emit('items-updated', items.value);
};

// Schedule task from line item
const scheduleTask = (item: WorkOrderItemModel) => {
    selectedItemForSchedule.value = item;
    showAssignmentModal.value = true;
};

// Open "From Job" modal
const openFromJobModal = () => {
    showFromJobModal.value = true;
};

// Handle assignment modal close
const onAssignmentClose = () => {
    showAssignmentModal.value = false;
    selectedItemForSchedule.value = null;
};

// Handle from job modal close
const onFromJobClose = () => {
    showFromJobModal.value = false;
    loadItems();  // Refresh items after adding from job
};

// Check if there are unsaved items (items currently being edited)
const hasUnsavedItems = computed(() => {
    const editing = items.value.filter(item => item._isEditing === true);
    console.log('[WorkOrderItemsDatatable] hasUnsavedItems computed - items:', items.value.length, 'editing:', editing.length);
    return editing.length > 0;
});

// Watch for changes to recalculate totals
watch(items, () => {
    emitItemsUpdate();
}, { deep: true });

// Expose hasUnsavedItems so parent can check before closing
defineExpose({
    hasUnsavedItems
});
</script>

<style scoped>
.table-responsive {
    max-height: 500px;
    overflow-y: auto;
}

.table-warning {
    background-color: #fff3cd !important;
}

input.form-control-sm,
select.form-select-sm {
    font-size: 0.875rem;
}
</style>
